import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdAccountBalance, MdCheck, MdWarning, MdLocationOn, MdAssessment, MdLocationCity, MdMap, MdPublic, MdCreditCard } from 'react-icons/md';
import ProfilePictureCropper from './ProfilePictureCropper';
import RepProfileViewMode from './rep-profile/RepProfileViewMode';
import CreateRepProfileModal from './rep-profile/CreateRepProfileModal';
import CloseRepProfileModal from './rep-profile/CloseRepProfileModal';

const sanitizeBioInput = (value) => {
  if (!value) return value;
  return value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/(--|;|\/\*|\*\/|xp_|sp_|0x)/gi, '')
    .replace(/(\bUNION\b|\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bEXEC\b|\bEXECUTE\b)/gi, '');
};

function RepresentativeProfileSection({ onProfileChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showPictureCropper, setShowPictureCropper] = useState(false);
  const [offices, setOffices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userLocation, setUserLocation] = useState({ state: null, city: null });
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [editData, setEditData] = useState({
    first_name: '', last_name: '', primary_email: '',
    office_level: '', office_id: '', running_state: '', running_city_county: ''
  });
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', primary_email: '', secondary_email: '',
    secondary_email_public: false, biography: '', office_level: '', office_id: '',
    running_state: '', running_city_county: '',
    disclaimer_acknowledged: false, poll_responses_public_acknowledged: false
  });
  const [showSubscriptionPicker, setShowSubscriptionPicker] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subPromoCode, setSubPromoCode] = useState('');
  const [subPromoStatus, setSubPromoStatus] = useState(null);

  useEffect(() => {
    loadProfile();
    loadOffices();
    loadUserLocation();
    loadSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscription/plans`),
        axios.get(`${API_URL}/api/subscription/my`),
      ]);
      setSubscriptionPlans(plansRes.data.plans || []);
      setSubscriptionStatus(statusRes.data.has_subscription ? statusRes.data.subscription : null);
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    }
  };

  const loadUserLocation = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile`);
      setUserLocation({ state: response.data.state, city: response.data.city });
    } catch (err) {
      console.error('Failed to load user location:', err);
    } finally {
      setLocationLoaded(true);
    }
  };

  const hasCompleteLocation = () => userLocation.state && userLocation.city;

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/representative/profile`);
      setHasProfile(response.data.has_profile);
      setProfile(response.data.profile);
      if (onProfileChange) onProfileChange(response.data.has_profile);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOffices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/offices`);
      setOffices(response.data.offices_by_level || {});
    } catch (err) {
      console.error('Failed to load offices:', err);
    }
  };

  // --- Create Modal Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'biography' ? sanitizeBioInput(value) : value
    }));
    if (name === 'office_level') {
      setFormData(prev => ({ ...prev, office_id: '', running_city_county: '' }));
    }
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) return 'First name is required';
    if (!formData.last_name.trim()) return 'Last name is required';
    if (!formData.primary_email.trim()) return 'Email is required';
    if (!formData.office_level) return 'Please select an office level';
    if (!formData.office_id) return 'Please select an office';
    if (!formData.running_state.trim()) return 'Please select the state where you are running';
    if (formData.office_level === 'Local' && !formData.running_city_county.trim()) {
      return 'Please enter the city or county where you are running for this local position';
    }
    if (!formData.disclaimer_acknowledged) return 'You must acknowledge the disclaimer';
    if (!formData.poll_responses_public_acknowledged) return 'You must acknowledge that poll responses will be public';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/representative/profile`, formData);
      setSuccess('Representative profile created! Now select a subscription plan.');
      setShowCreateModal(false);
      loadProfile();
      // Show subscription picker for new profiles
      setShowSubscriptionPicker(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to create profile');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscriptionCheckout = async (planId) => {
    setSelectedPlan(planId);
    setCheckoutLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
        promo_code: subPromoCode || null,
      });
      if (response.data.trial_activated) {
        setSubPromoStatus({ type: 'success', message: response.data.message });
        setTimeout(() => window.location.reload(), 2000);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start subscription checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const validateSubPromo = async () => {
    if (!subPromoCode.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/promo-codes/validate`, {
        code: subPromoCode, purchase_type: 'subscription',
      });
      setSubPromoStatus({ type: 'success', message: res.data.message, data: res.data });
    } catch (err) {
      setSubPromoStatus({ type: 'error', message: err.response?.data?.detail || 'Invalid code' });
    }
  };

  // --- Close Profile ---
  const handleCloseProfile = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/representative/profile/close`);
      setSuccess('Representative profile closed successfully');
      setShowCloseModal(false);
      loadProfile();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to close profile');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Profile Picture ---
  const handlePictureUpload = async (imageDataUrl) => {
    try {
      const response = await axios.post(`${API_URL}/api/representative/profile/picture/upload`, {
        image_data: imageDataUrl, content_type: 'image/jpeg'
      });
      setProfile(prev => ({ ...prev, profile_picture_url: response.data.url }));
      setSuccess('Profile picture updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to upload image');
    }
  };

  // --- Bio Editing ---
  const handleSaveBio = async () => {
    try {
      setSubmitting(true);
      setError('');
      await axios.put(`${API_URL}/api/representative/profile`, { biography: bioText });
      setProfile(prev => ({ ...prev, biography: bioText }));
      setEditingBio(false);
      setSuccess('Biography updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update biography');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Profile Editing ---
  const startEditingProfile = () => {
    setEditData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      primary_email: profile.primary_email || '',
      office_level: profile.office?.level || '',
      office_id: profile.office?.office_id || '',
      running_state: profile.running_state || '',
      running_city_county: profile.running_city_county || ''
    });
    setEditingProfile(true);
    setError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
    if (name === 'office_level') {
      setEditData(prev => ({ ...prev, office_id: '', running_city_county: '' }));
    }
  };

  const handleSaveProfile = async () => {
    if (!editData.first_name.trim()) { setError('First name is required'); return; }
    if (!editData.last_name.trim()) { setError('Last name is required'); return; }
    if (!editData.primary_email.trim()) { setError('Email is required'); return; }
    if (editData.office_level && !editData.office_id) { setError('Please select an office'); return; }
    if (editData.office_level === 'Local' && !editData.running_city_county.trim()) {
      setError('City/County is required for local positions'); return;
    }
    try {
      setSubmitting(true);
      setError('');
      const payload = {
        first_name: editData.first_name,
        last_name: editData.last_name,
        primary_email: editData.primary_email
      };
      if (editData.office_id) payload.office_id = editData.office_id;
      if (editData.running_state) payload.running_state = editData.running_state;
      if (editData.running_city_county !== undefined) payload.running_city_county = editData.running_city_county || '';
      await axios.put(`${API_URL}/api/representative/profile`, payload);
      setEditingProfile(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadProfile();
      loadOffices();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to update profile');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="rep-profile-section">
        <h2 className="text-xl font-semibold text-gray-900 font-rubik mb-4 flex items-center gap-2">
          <MdAccountBalance className="text-primary" />
          Representative Profile
        </h2>

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
            <MdCheck className="text-lg" />
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <MdWarning className="text-lg" />
            {error}
          </div>
        )}

        {hasProfile && profile ? (
          <RepProfileViewMode
            profile={profile}
            offices={offices}
            editingProfile={editingProfile}
            editData={editData}
            submitting={submitting}
            onStartEditing={startEditingProfile}
            onEditChange={handleEditChange}
            onSaveProfile={handleSaveProfile}
            onCancelEdit={() => { setEditingProfile(false); setError(''); }}
            onEditBio={() => { setBioText(profile.biography || ''); setEditingBio(true); }}
            onSaveBio={handleSaveBio}
            editingBio={editingBio}
            bioText={bioText}
            onBioTextChange={setBioText}
            onCancelBio={() => setEditingBio(false)}
            sanitizeBioInput={sanitizeBioInput}
            onOpenPictureCropper={() => setShowPictureCropper(true)}
            onCloseProfile={() => setShowCloseModal(true)}
          />
        ) : (
          <div className="text-center py-6">
            <MdAccountBalance className="text-5xl text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-2">No Representative Profile</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              Create a representative profile to share your political positions with voters
              and appear in search results.
            </p>

            {locationLoaded && !hasCompleteLocation() ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto mb-4">
                <div className="flex items-start gap-3">
                  <MdLocationOn className="text-2xl text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <h4 className="font-medium text-amber-800 mb-1">Location Required</h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Before creating a Representative Profile, you must set your <strong>state</strong> and <strong>city</strong> in your personal profile.
                    </p>
                    <p className="text-sm text-amber-600">
                      Current location: {userLocation.state || 'Not set'}{userLocation.city ? `, ${userLocation.city}` : ' (City not set)'}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-amber-700 mt-3">
                  Please update your location in the profile form above and click <strong>Save Profile</strong>, then return here to create your Representative Profile.
                </p>
              </div>
            ) : null}

            <button
              onClick={() => setShowCreateModal(true)}
              disabled={!hasCompleteLocation()}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                hasCompleteLocation()
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="create-rep-profile-btn"
            >
              Create Representative Profile
            </button>
          </div>
        )}
      </div>

      {/* Subscription Picker for new profiles */}
      {showSubscriptionPicker && hasProfile && !subscriptionStatus && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="subscription-picker">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <MdCreditCard className="text-primary text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 font-rubik">Choose Your Subscription Plan</h3>
              <p className="text-sm text-gray-500 font-rubik">A subscription is required to maintain your representative profile.</p>
            </div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4 font-rubik">{error}</div>}
          <div className="flex items-end gap-2 mb-4" data-testid="sub-promo-section">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 mb-1 font-rubik">Promo Code</label>
              <input
                type="text"
                value={subPromoCode}
                onChange={e => { setSubPromoCode(e.target.value.toUpperCase()); setSubPromoStatus(null); }}
                placeholder="Enter promo code"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                data-testid="sub-promo-input"
              />
            </div>
            <button
              onClick={validateSubPromo}
              disabled={!subPromoCode.trim()}
              className="px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors"
              data-testid="sub-promo-apply-btn"
            >
              Apply
            </button>
          </div>
          {subPromoStatus && (
            <div className={`text-sm rounded-lg p-3 mb-4 font-rubik ${subPromoStatus.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="sub-promo-status">
              {subPromoStatus.message}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {subscriptionPlans.map((plan) => (
              <div key={plan.id} className={`border-2 rounded-xl p-5 cursor-pointer transition-all hover:border-primary hover:shadow-md ${selectedPlan === plan.id ? 'border-primary bg-primary/5' : 'border-gray-200'}`} data-testid={`sub-plan-card-${plan.id}`}>
                <h4 className="text-base font-semibold text-gray-800 font-rubik">{plan.name}</h4>
                <p className="text-xs text-gray-500 font-rubik mt-1">{plan.description}</p>
                <div className="text-2xl font-bold text-primary font-rubik mt-3 mb-4">${plan.amount.toFixed(2)}<span className="text-sm font-normal text-gray-500">/{plan.interval_months === 1 ? 'month' : plan.interval_months === 3 ? 'quarter' : 'year'}</span></div>
                <button
                  onClick={() => handleSubscriptionCheckout(plan.id)}
                  disabled={checkoutLoading && selectedPlan === plan.id}
                  className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium font-rubik disabled:opacity-50"
                  data-testid={`subscribe-btn-${plan.id}`}
                >
                  {checkoutLoading && selectedPlan === plan.id ? 'Processing...' : 'Subscribe'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Management for existing subscribers */}
      {hasProfile && subscriptionStatus && !subscriptionStatus.billing_exempt && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="subscription-info-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <MdCreditCard className="text-green-600 text-xl" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 font-rubik">Subscription: {subscriptionStatus.plan_name}</h3>
                <p className="text-xs text-gray-500 font-rubik">
                  {subscriptionStatus.status === 'active' ? `Renews on ${new Date(subscriptionStatus.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` :
                   subscriptionStatus.status === 'cancelling' ? `Ends on ${new Date(subscriptionStatus.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` :
                   subscriptionStatus.status}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/transactions?tab=subscriptions')}
              className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-rubik transition-colors"
              data-testid="manage-subscription-btn"
            >
              Manage
            </button>
          </div>
        </div>
      )}

      {/* Prompt unsubscribed reps to subscribe (not shown right after creation) */}
      {hasProfile && !subscriptionStatus && !showSubscriptionPicker && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-6" data-testid="subscription-required-notice">
          <div className="flex items-start gap-3">
            <MdWarning className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800 font-rubik">Subscription Required</h3>
              <p className="text-xs text-amber-700 font-rubik mt-1">A subscription is required to maintain your representative profile. Please select a plan to continue.</p>
              <button onClick={() => setShowSubscriptionPicker(true)} className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-rubik" data-testid="show-subscription-plans-btn">View Plans</button>
            </div>
          </div>
        </div>
      )}

      {/* Candidacy Assessment CTA */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="candidacy-assessment-cta">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-tertiary/10 flex items-center justify-center flex-shrink-0">
            <MdAssessment className="text-tertiary text-2xl" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 font-rubik mb-1">Candidacy Assessment</h3>
            <p className="text-sm text-gray-600 font-rubik mb-4">
              See how your poll responses align with voters in your area. Get a detailed breakdown by topic
              to understand your candidacy potential at the local, state, or national level.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => navigate('/transactions?tab=candidacy')}
                className="flex items-center gap-2 px-4 py-2.5 bg-tertiary/10 text-tertiary rounded-lg hover:bg-tertiary/20 transition-colors font-rubik text-sm font-medium"
                data-testid="candidacy-cta-local"
              >
                <MdLocationCity className="text-lg" />
                Local Report
              </button>
              <button
                onClick={() => navigate('/transactions?tab=candidacy')}
                className="flex items-center gap-2 px-4 py-2.5 bg-tertiary/10 text-tertiary rounded-lg hover:bg-tertiary/20 transition-colors font-rubik text-sm font-medium"
                data-testid="candidacy-cta-state"
              >
                <MdMap className="text-lg" />
                State Report
              </button>
              <button
                onClick={() => navigate('/transactions?tab=candidacy')}
                className="flex items-center gap-2 px-4 py-2.5 bg-tertiary/10 text-tertiary rounded-lg hover:bg-tertiary/20 transition-colors font-rubik text-sm font-medium"
                data-testid="candidacy-cta-national"
              >
                <MdPublic className="text-lg" />
                National Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateRepProfileModal
          formData={formData}
          offices={offices}
          submitting={submitting}
          error={error}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showCloseModal && (
        <CloseRepProfileModal
          submitting={submitting}
          onConfirm={handleCloseProfile}
          onCancel={() => setShowCloseModal(false)}
        />
      )}

      {showPictureCropper && (
        <ProfilePictureCropper
          onUpload={handlePictureUpload}
          onClose={() => setShowPictureCropper(false)}
          currentPictureUrl={profile?.profile_picture_url}
        />
      )}
    </>
  );
}

export default RepresentativeProfileSection;
